import { create } from 'zustand';
import type { GameState, Player, User } from '@shared/types';

interface GameStore {
  // Authentication
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;

  // Game state
  gameState: GameState | null;
  setGameState: (state: GameState) => void;
  updateGameState: (updates: Partial<GameState>) => void;

  // Connection
  isConnected: boolean;
  setConnected: (connected: boolean) => void;
  reconnectAttempts: number;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;

  // UI state
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;

  // Helpers
  isHost: () => boolean;
  isGuesser: () => boolean;
  getCurrentPlayer: () => Player | null;
  reset: () => void;
}

const useGameStore = create<GameStore>((set, get) => ({
  // Authentication
  user: null,
  isAuthenticated: false,
  setUser: (user) => set({ user, isAuthenticated: !!user }),

  // Game state
  gameState: null,
  setGameState: (gameState) => set({ gameState }),
  updateGameState: (updates) =>
    set((state) => ({
      gameState: state.gameState ? { ...state.gameState, ...updates } : null,
    })),

  // Connection
  isConnected: false,
  setConnected: (isConnected) => set({ isConnected }),
  reconnectAttempts: 0,
  incrementReconnectAttempts: () =>
    set((state) => ({
      reconnectAttempts: state.reconnectAttempts + 1,
    })),
  resetReconnectAttempts: () => set({ reconnectAttempts: 0 }),

  // UI state
  isLoading: false,
  setLoading: (isLoading) => set({ isLoading }),
  error: null,
  setError: (error) => set({ error }),

  // Helpers
  isHost: () => {
    const { user, gameState } = get();
    return user?.id === gameState?.hostId;
  },
  isGuesser: () => {
    const { user, gameState } = get();
    return user?.id === gameState?.round?.activeGuesserId;
  },
  getCurrentPlayer: () => {
    const { user, gameState } = get();
    if (!user || !gameState) return null;
    return gameState.players[user.id] || null;
  },
  reset: () =>
    set({
      gameState: null,
      isConnected: false,
      reconnectAttempts: 0,
      isLoading: false,
      error: null,
    }),
}));

export default useGameStore;
